import { useState } from 'react';
import { Download, Languages } from 'lucide-react';

import { AuthorFilter } from '../../components/AuthorFilter';
import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  FilePicker,
  Skeleton,
  Toggle,
} from '../../components/ui';
import {
  TRANSLATE_ACCEPT,
  isTranslateRunActive,
  taskProgressPct,
  useCurrentUser,
  useStartTranslate,
  useToolTask,
  useTranslateRun,
  useTranslateRuns,
} from '../../hooks';
import { authorLabel } from '../../lib/author';
import { downloadFile } from '../../lib/download';
import { formatDateTime as formatWhen } from '../tts/format';
import type { Id, TranslateRun } from '../../types/api';
import styles from './Translate.module.css';

export function TranslatePage() {
  const { can } = useCurrentUser();
  const canEdit = can('editor');

  const [file, setFile] = useState<File | null>(null);
  const [twoWords, setTwoWords] = useState(false);
  const [openId, setOpenId] = useState<Id | null>(null);

  const start = useStartTranslate();
  const [authorFilter, setAuthorFilter] = useState('');
  const runs = useTranslateRuns({ limit: 20, author: authorFilter || undefined });
  const opened = useTranslateRun(openId ?? undefined);

  const run = opened.data;
  const task = useToolTask(run?.task_id ?? undefined, {
    enabled: isTranslateRunActive(run),
  });

  const submit = () => {
    if (!file) return;
    start.mutate(
      { file, two_words: twoWords },
      {
        onSuccess: (created) => {
          setOpenId(created.id);
          setFile(null);
        },
      },
    );
  };

  return (
    <div className={styles.page}>
      <Card>
        <CardHead
          title="Перевод субтитров"
          hint="Ролик, звуковая дорожка или готовый .srt. На выходе русский .srt с теми же таймингами"
        />
        <CardBody pad>
          <div className={styles.form}>
            <FilePicker
              file={file}
              onPick={setFile}
              accept={TRANSLATE_ACCEPT}
              disabled={!canEdit || start.isPending}
              buttonLabel="Выбрать файл"
              emptyLabel="ролик, аудио или .srt"
              ariaLabel="Файл для перевода"
            />
            <Toggle
              checked={twoWords}
              onChange={setTwoWords}
              disabled={!canEdit || start.isPending}
              label="По два слова в субтитре"
            />
            <Button
              variant="primary"
              onClick={submit}
              disabled={!canEdit || !file || start.isPending}
            >
              {start.isPending ? 'Загружаем…' : 'Перевести'}
            </Button>
          </div>
          {!canEdit && <p className="muted">Роль «наблюдатель»: перевод может запустить редактор.</p>}
          {start.error && <ErrorBanner error={start.error} />}
        </CardBody>
      </Card>

      {run && (
        <RunCard
          run={run}
          progressPct={taskProgressPct(task.data)}
          stage={String(task.data?.progress?.stage ?? '')}
        />
      )}

      <Card>
        <CardHead
          title="Прошлые переводы"
          actions={<AuthorFilter value={authorFilter} onChange={setAuthorFilter} />}
        />
        <CardBody pad>
          {runs.isLoading && <Skeleton width="60%" height={11} />}
          {runs.error && <ErrorBanner error={runs.error} onRetry={() => void runs.refetch()} />}
          {!runs.isLoading && (runs.data?.items.length ?? 0) === 0 && (
            <EmptyState
              icon={<Languages size={18} strokeWidth={1.5} />}
              title="Переводов пока нет"
              description="Загрузите ролик или готовый файл субтитров — вернём русский .srt."
            />
          )}
          <div className={styles.history}>
            {(runs.data?.items ?? []).map((item) => (
              <button
                key={item.id}
                type="button"
                className={item.id === openId ? `${styles.row} ${styles.rowOpen}` : styles.row}
                onClick={() => setOpenId(item.id)}
              >
                <span className={styles.rowTitle}>{item.title}</span>
                <span className={styles.rowMeta}>
                  {describe(item)} · {authorLabel(item.author)} · {formatWhen(item.created_at)}
                </span>
              </button>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function RunCard({
  run,
  progressPct,
  stage,
}: {
  run: TranslateRun;
  progressPct: number | null;
  stage: string;
}) {
  const active = isTranslateRunActive(run);

  return (
    <Card>
      <CardHead
        title={run.title}
        hint={describe(run)}
        actions={
          run.srt?.url ? (
            <Button
              size="sm"
              variant="primary"
              onClick={() => void downloadFile(run.srt?.url ?? '', srtName(run))}
            >
              <Download size={14} strokeWidth={1.5} />
              Скачать .srt
            </Button>
          ) : undefined
        }
      />
      <CardBody pad>
        {active && (
          <p className={styles.stage}>
            {stage || 'идёт работа'}
            {progressPct === null ? '…' : ` — ${progressPct}%`}
          </p>
        )}

        {run.status === 'failed' && <div className="error-banner">{run.error ?? 'Не получилось'}</div>}

        {run.preview.length > 0 && (
          <table className={styles.preview}>
            <tbody>
              {run.preview.map((cue) => (
                <tr key={cue.start_ms}>
                  <td className={styles.time}>{clock(cue.start_ms)}</td>
                  <td className={styles.source}>{cue.source}</td>
                  <td className={styles.text}>{cue.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {run.status === 'succeeded' && run.cues_total > run.preview.length && (
          <p className="muted">
            Показаны первые {run.preview.length} реплик из {run.cues_total}. Остальные — в файле.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function srtName(run: TranslateRun): string {
  const stem = run.title.replace(/\.[^.]+$/, '') || 'subtitles';
  return `${stem}.${run.target_language}.srt`;
}

function describe(run: TranslateRun): string {
  if (run.status === 'succeeded') {
    const mode = run.two_words ? ' · по два слова' : '';
    return `${run.cues_total} реплик · ${run.target_language}${mode}`;
  }
  if (run.status === 'failed') return 'не получилось';
  return run.source_kind === 'srt' ? 'переводим субтитры' : 'распознаём и переводим';
}

function clock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
